import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Zap, Loader2, CheckCircle2, ArrowRight, ArrowLeft, Sparkles, Workflow, Bot } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  
  // Onboarding data
  const [companyInfo, setCompanyInfo] = useState({
    industry: "",
    company_size: "",
    use_case: ""
  });
  
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const industries = [
    "E-commerce",
    "SaaS",
    "Healthcare",
    "Finance",
    "Education",
    "Real Estate",
    "Marketing",
    "Other"
  ];

  const companySizes = [
    "1-10 employees",
    "11-50 employees",
    "51-200 employees",
    "201-500 employees",
    "500+ employees"
  ];

  const useCases = [
    "Customer Support Automation",
    "Sales & Lead Generation",
    "Data Processing & ETL",
    "Marketing Automation",
    "Internal Workflows",
    "API Integrations",
    "Other"
  ];

  const templates = [
    {
      id: "customer-support",
      name: "Customer Support Bot",
      description: "Automate customer inquiries with AI-powered responses",
      icon: Bot
    },
    {
      id: "lead-generation",
      name: "Lead Generation Flow",
      description: "Capture and qualify leads automatically",
      icon: Sparkles
    },
    {
      id: "data-sync",
      name: "Data Synchronization",
      description: "Keep your data in sync across multiple platforms",
      icon: Workflow
    }
  ];

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const handleComplete = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/customer/auth/complete-onboarding`, {
        ...companyInfo,
        selected_template: selectedTemplate
      }, { withCredentials: true });

      if (response.data.success) {
        navigate('/dashboard');
      } else {
        setError(response.data.error || "Failed to complete onboarding");
      }
    } catch (err) {
      console.error("Onboarding error:", err);
      setError(err.response?.data?.error || err.response?.data?.detail || "Failed to complete onboarding");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-heading font-semibold text-white mb-2">
                Welcome to QuantumFlowSync!
              </h2>
              <p className="text-slate-400 text-lg">
                Let's get you set up in just a few steps
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-3xl font-bold text-white mb-1">14</div>
                <div className="text-slate-400 text-sm">Days Free Trial</div>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-3xl font-bold text-white mb-1">100</div>
                <div className="text-slate-400 text-sm">Free Minutes</div>
              </div>
              <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
                <div className="text-3xl font-bold text-white mb-1">∞</div>
                <div className="text-slate-400 text-sm">Workflows</div>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <h3 className="text-blue-400 font-medium mb-2">What you'll get:</h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Access to all premium features during your trial</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>Pre-built workflow templates to get started quickly</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                  <span>24/7 customer support and documentation</span>
                </li>
              </ul>
            </div>

            <Button
              onClick={handleNext}
              className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium"
            >
              Get Started
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                Tell us about your company
              </h2>
              <p className="text-slate-400">
                This helps us personalize your experience
              </p>
            </div>

            <div>
              <Label htmlFor="industry" className="text-white">Industry</Label>
              <select
                id="industry"
                value={companyInfo.industry}
                onChange={(e) => setCompanyInfo({ ...companyInfo, industry: e.target.value })}
                className="mt-1 w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select your industry</option>
                {industries.map((industry) => (
                  <option key={industry} value={industry} className="bg-navy-500">
                    {industry}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="company_size" className="text-white">Company Size</Label>
              <select
                id="company_size"
                value={companyInfo.company_size}
                onChange={(e) => setCompanyInfo({ ...companyInfo, company_size: e.target.value })}
                className="mt-1 w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select company size</option>
                {companySizes.map((size) => (
                  <option key={size} value={size} className="bg-navy-500">
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="use_case" className="text-white">Primary Use Case</Label>
              <select
                id="use_case"
                value={companyInfo.use_case}
                onChange={(e) => setCompanyInfo({ ...companyInfo, use_case: e.target.value })}
                className="mt-1 w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select your primary use case</option>
                {useCases.map((useCase) => (
                  <option key={useCase} value={useCase} className="bg-navy-500">
                    {useCase}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12 rounded-full border-white/10 bg-white/5 hover:bg-white/10 text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!companyInfo.industry || !companyInfo.company_size || !companyInfo.use_case}
                className="flex-1 h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                Choose a template to get started
              </h2>
              <p className="text-slate-400">
                Or skip this step and build from scratch
              </p>
            </div>

            <div className="space-y-3">
              {templates.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                    className={`w-full p-4 rounded-lg border transition-all text-left ${
                      selectedTemplate === template.id
                        ? 'bg-blue-500/10 border-blue-500/50'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        selectedTemplate === template.id
                          ? 'bg-blue-500/20 border border-blue-500/30'
                          : 'bg-white/5 border border-white/10'
                      }`}>
                        <Icon className={`w-5 h-5 ${
                          selectedTemplate === template.id ? 'text-blue-400' : 'text-slate-400'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-medium mb-1">{template.name}</h3>
                        <p className="text-slate-400 text-sm">{template.description}</p>
                      </div>
                      {selectedTemplate === template.id && (
                        <CheckCircle2 className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 h-12 rounded-full border-white/10 bg-white/5 hover:bg-white/10 text-white"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="flex-1 h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium"
              >
                Continue
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-heading font-semibold text-white mb-2">
                You're all set!
              </h2>
              <p className="text-slate-400">
                Your account is ready. Let's start building workflows!
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-lg p-6 space-y-4">
              <h3 className="text-white font-medium mb-3">What's next?</h3>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-medium text-sm">1</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Explore the Dashboard</h4>
                  <p className="text-slate-400 text-sm">Get familiar with the interface and available features</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-medium text-sm">2</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Create Your First Workflow</h4>
                  <p className="text-slate-400 text-sm">Use our templates or build from scratch</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-400 font-medium text-sm">3</span>
                </div>
                <div>
                  <h4 className="text-white font-medium mb-1">Connect Integrations</h4>
                  <p className="text-slate-400 text-sm">Link your favorite tools and services</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                {error}
              </div>
            )}

            <Button
              onClick={handleComplete}
              disabled={loading}
              className="w-full h-12 rounded-full gradient-active hover:opacity-90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Setting up...
                </>
              ) : (
                <>
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="glass-heavy rounded-3xl p-8 border border-white/10">
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3, 4].map((step) => (
                <div key={step} className="flex items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-medium transition-all ${
                      currentStep >= step
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/5 border border-white/10 text-slate-500'
                    }`}
                  >
                    {currentStep > step ? <CheckCircle2 className="w-5 h-5" /> : step}
                  </div>
                  {step < 4 && (
                    <div
                      className={`h-1 flex-1 mx-2 rounded-full transition-all ${
                        currentStep > step ? 'bg-blue-500' : 'bg-white/10'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-between text-xs text-slate-500">
              <span>Welcome</span>
              <span>Company Info</span>
              <span>Template</span>
              <span>Complete</span>
            </div>
          </div>

          {/* Step Content */}
          {renderStep()}

          {/* Skip Button */}
          {currentStep < 4 && (
            <div className="mt-6 text-center">
              <button
                onClick={handleSkip}
                className="text-slate-400 hover:text-white transition-colors text-sm"
              >
                Skip onboarding →
              </button>
            </div>
          )}
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-slate-400 hover:text-white transition-colors text-sm"
          >
            ← Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
